import React, { FC, useEffect, useState } from 'react';
import { Box, createStyles } from '@material-ui/core';
import { BlogArticleFragment } from '../../../../../__generated__/gatsby-types';
import ArticleCard from '../../../article-card';
import SpotlightCard from '../../../spotlight-card';
import { makeStyles } from '@material-ui/core/styles';
import PgeButton from '../../../../pge-button/_PgeButton';
import SocialFollowLinks from '../../../social-follow-links';
import { useTranslation } from '../../../../../hooks/useTranslation';
import Helmet from 'react-helmet';
import colors from '../../../../../themes/main-colors';

const useStyles = makeStyles(() =>
  createStyles({
    articles: {
      display: 'flex',
      gap: '30px',
    },
    columnBox: {
      display: 'flex',
      flexDirection: 'column',
      gap: '30px',
    },
    hr: {
      margin: '30px 0',
      color: colors.lightGray1,
      backgroundColor: colors.lightGray1,
      borderColor: colors.lightGray1,
      borderStyle: 'solid',
    },
    fontWeight: {
      fontWeight: 600,
    },
  }),
);

interface Props {
  articles: BlogArticleFragment[];
  spotlightArticle?: BlogArticleFragment;
}

const Desktop: FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { articles, spotlightArticle } = props;
  const hasSpotlightArticle = !!spotlightArticle?.id;

  const articlesToShowOnload = 8;

  const [leftColumn, setLeftColumn] = useState<BlogArticleFragment[]>([]);
  const [rightColumn, setRightColumn] = useState<BlogArticleFragment[]>([]);
  const [isShowMoreButtonDisplayed, setIsShowMoreButtonDisplayed] = useState(
    false,
  );
  const [nextArticle, setNextArticle] = useState<number>(articlesToShowOnload);

  useEffect(() => {
    if (articles && articles.length > 0) {
      // Distribute articles between the two columns from left to right and top to bottom
      // maintaining order of the date sort in the GQL.
      // If a spotlight article exists, take it out of the flow and pin it as the second
      // item in the right column.

      const articlesToShowCount = hasSpotlightArticle
        ? articlesToShowOnload - 1
        : articlesToShowOnload;
      const articlesToShow = articles.slice(0, articlesToShowCount);

      const leftColumnArticles: BlogArticleFragment[] = hasSpotlightArticle
        ? [articlesToShow[0], ...articlesToShow.slice(2, 6)]
        : articlesToShow.slice(0, Math.ceil(articlesToShow.length / 2));

      const rightColumnArticles: BlogArticleFragment[] = hasSpotlightArticle
        ? articlesToShow.length > 5
          ? [articlesToShow[1], spotlightArticle, articlesToShow[6]]
          : [articlesToShow[1], spotlightArticle]
        : articlesToShow.slice(Math.ceil(articlesToShow.length / 2));

      setLeftColumn(leftColumnArticles);
      setRightColumn(rightColumnArticles);

      setIsShowMoreButtonDisplayed(
        (hasSpotlightArticle
          ? articlesToShowOnload - 1
          : articlesToShowOnload) < articles.length,
      );
    }
  }, []);

  const handleLoadMoreArticles = () => {
    const leftColumnUpdate: BlogArticleFragment[] = [];
    const rightColumnUpdate: BlogArticleFragment[] = [];
    const columnUpdates = [leftColumnUpdate, rightColumnUpdate];

    const newArticleIndex = nextArticle + 6;
    const moreArticles = articles.slice(nextArticle, newArticleIndex);
    moreArticles.forEach((article, index) =>
      columnUpdates[index % 2].push(article),
    );

    setLeftColumn(current => current.concat(leftColumnUpdate));
    setRightColumn(current => current.concat(rightColumnUpdate));
    setNextArticle(newArticleIndex);
    setIsShowMoreButtonDisplayed(newArticleIndex < articles.length);
  };

  return (
    <>
      <Helmet>
        {/* UI hack: force a horizonal scrollbar to reduce the
            perception of a 'flicker' on page load in desktop mode */}
        <style type="text/css">{`
          body {
              overflow-y: scroll;
          }
      `}</style>
      </Helmet>
      <Box className={classes.articles}>
        <Box className={classes.columnBox}>
          {leftColumn.map((item: BlogArticleFragment, index: number) => (
            <ArticleCard item={item} />
          ))}
        </Box>
        <Box className={classes.columnBox}>
          {rightColumn.map((item: BlogArticleFragment, index: number) =>
            hasSpotlightArticle && index === 1 ? (
              <SpotlightCard item={item} />
            ) : (
              <ArticleCard item={item} />
            ),
          )}
        </Box>
      </Box>
      <hr className={classes.hr} />
      <Box
        display={'flex'}
        justifyContent={
          isShowMoreButtonDisplayed ? 'space-between' : 'flex-end'
        }
        flexDirection={'row'}
        alignItems={'center'}
        mt={2.5}
        mb={2.5}
      >
        {isShowMoreButtonDisplayed ? (
          <PgeButton
            theme="Secondary"
            className={classes.fontWeight}
            onClick={handleLoadMoreArticles}
          >
            {t('SHOW_MORE')}
          </PgeButton>
        ) : null}
        <SocialFollowLinks />
      </Box>
    </>
  );
};

export default Desktop;
